import { useState, useMemo } from "react";
import { PoundSterling, TrendingDown, Download, Percent, CalendarClock, AlertTriangle, ShieldAlert, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/utils/dateFormat";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import BankBalanceCard from "@/components/BankBalanceCard";
import { useDisplay } from "@/context/DisplayContext";

const fmt = (v) => {
  if (v === undefined || v === null) return "£0.00";
  return `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function useChartColors() {
  const { theme } = useDisplay();
  if (theme === "high-contrast") return { barPrimary: "#FDE047", barPaid: "#4ade80", barTrack: "#3F3F46", grid: "#3F3F46", text: "#FFFFFF", positive: "#4ade80", negative: "#FBBF24" };
  if (theme === "dark") return { barPrimary: "#F9FAFB", barPaid: "#4ade80", barTrack: "#374151", grid: "rgba(255,255,255,0.06)", text: "#9CA3AF", positive: "#4ade80", negative: "#f87171" };
  return { barPrimary: "#000000", barPaid: "#16a34a", barTrack: "#E5E7EB", grid: "#F3F4F6", text: "#374151", positive: "#16a34a", negative: "#dc2626" };
}

const PIE_COLORS_LIGHT = ["#16a34a", "#dc2626", "#2563eb"];
const PIE_COLORS_DARK = ["#4ade80", "#f87171", "#60a5fa"];
const PIE_COLORS_HC = ["#4ade80", "#FBBF24", "#60a5fa"];

function ProgressRing({ pct, size = 120, strokeWidth = 10 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const { theme } = useDisplay();
  const trackColor = theme === "high-contrast" ? "#3F3F46" : theme === "dark" ? "#374151" : "#E5E7EB";
  const ringColor = pct > 0 ? (theme === "high-contrast" ? "#4ade80" : "#16a34a") : trackColor;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
      <circle
        cx={size/2} cy={size/2} r={radius} fill="none"
        stroke={ringColor} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: "stroke-dashoffset 0.8s ease" }}
      />
    </svg>
  );
}

const StatCard = ({ title, value, sub, icon: Icon, variant, testId }) => {
  const colorMap = { danger: "border-red-200", success: "border-green-200", warning: "border-amber-200", info: "border-blue-200", default: "border-gray-200" };
  return (
    <div data-testid={testId} className={`bg-white border ${colorMap[variant] || colorMap.default} rounded-sm p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{title}</span>
        <Icon className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
      </div>
      <p className="font-mono-data text-2xl font-semibold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1 font-mono-data">{sub}</p>}
    </div>
  );
};

function PromoProgressBar({ promo_until, promo_status }) {
  if (!promo_until || promo_status === "none") return null;
  const month_map = {Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12};
  try {
    const parts = promo_until.split("-");
    const mon = month_map[parts[0]] || 1;
    const yr = parseInt(parts[1]) + 2000;
    const expiry = new Date(yr, mon - 1, 1);
    const now = new Date();
    const daysLeft = Math.max(0, Math.floor((expiry - now) / (1000 * 60 * 60 * 24)));
    const monthsLeft = Math.round(daysLeft / 30);
    const totalDays = 365 * 2;
    const pct = Math.max(0, Math.min(100, ((totalDays - daysLeft) / totalDays) * 100));
    const pctRemaining = 100 - Math.round(pct);
    let color = "bg-green-500";
    let label = `${pctRemaining}% of 0% period remaining (${monthsLeft} months)`;
    if (daysLeft <= 30) { color = "bg-red-500 animate-pulse"; label = `URGENT: ${daysLeft} days until 0% expires!`; }
    else if (daysLeft <= 90) { color = "bg-amber-500"; label = `${daysLeft} days left — ${monthsLeft} months until interest kicks in`; }
    return (
      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1 border border-gray-200" title={label}>
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    );
  } catch { return null; }
}

function exportCSV(data, filename, columns) {
  const header = columns.map(c => c.label).join(",");
  const rows = data.map(row => columns.map(c => `"${row[c.key] ?? ""}"`).join(","));
  const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function DashboardPage({ dashboard, debts, budget, loading, api }) {
  const { theme } = useDisplay();
  const cc = useChartColors();
  const pieColors = theme === "high-contrast" ? PIE_COLORS_HC : theme === "dark" ? PIE_COLORS_DARK : PIE_COLORS_LIGHT;
  const [leakOpen, setLeakOpen] = useState(false);

  // Estimated next min (dynamic for percentage debts) — must be before early return
  const estNextMin = useMemo(() => {
    if (!debts || debts.length === 0) return 0;
    return debts.reduce((sum, d) => {
      if (d.min_payment_type === "percentage" && d.min_payment_pct > 0) {
        return sum + Math.max(d.current_balance * d.min_payment_pct / 100, d.min_payment_floor || 5);
      }
      return sum + d.statement_min;
    }, 0);
  }, [debts]);

  if (loading || !dashboard) {
    return <div data-testid="dashboard-loading" className="flex items-center justify-center h-64"><p className="text-gray-400 text-sm">Loading dashboard...</p></div>;
  }

  const topDebts = [...debts].sort((a, b) => (b.current_balance ?? b.total_balance ?? 0) - (a.current_balance ?? a.total_balance ?? 0)).slice(0, 5);
  const recentBudget = [...budget].slice(-6);
  const warnings = dashboard.promo_warnings || [];

  // Progress ring calculation
  const originalTotal = debts.reduce((s, d) => s + d.total_balance, 0);
  const currentTotal = debts.reduce((s, d) => s + d.current_balance, 0);
  const paidOff = Math.max(0, originalTotal - currentTotal);
  const paidPct = originalTotal > 0 ? Math.round((paidOff / originalTotal) * 100) : 0;

  const pieData = [
    { name: "Income", value: dashboard.total_income },
    { name: "Bills", value: dashboard.total_expenses },
    { name: "Debt Payments", value: dashboard.total_debt_payments || 0 },
  ].filter(d => d.value > 0);

  const debtBarData = debts.map(d => {
    const paid = Math.max(0, d.total_balance - d.current_balance);
    const pctGone = d.total_balance > 0 ? Math.round((paid / d.total_balance) * 100) : 0;
    return {
      name: d.name.length > 12 ? d.name.substring(0, 12) + ".." : d.name,
      remaining: d.current_balance,
      paid: paid,
      pctLabel: `${pctGone}%`,
    };
  });

  const exportDebts = () => exportCSV(debts, "the-vault-debts.csv", [
    { key: "name", label: "Debt Name" }, { key: "total_balance", label: "Total Balance" },
    { key: "apr_now", label: "APR Now" }, { key: "promo_until", label: "0% Until" },
    { key: "apr_after", label: "APR After" }, { key: "statement_min", label: "Stmt Min" },
    { key: "current_balance", label: "Current Balance" },
  ]);
  const exportBudget = () => exportCSV(budget, "the-vault-budget.csv", [
    { key: "date", label: "Date" }, { key: "description", label: "Description" },
    { key: "category", label: "Category" }, { key: "budgeted", label: "Budgeted" },
    { key: "actual", label: "Actual" }, { key: "variance", label: "Variance" },
    { key: "running_balance", label: "Running Balance" },
  ]);

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Overview of your debt and budget position</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="export-debts-btn" variant="outline" onClick={exportDebts} className="rounded-sm gap-1.5 text-xs">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />Debts CSV
          </Button>
          <Button data-testid="export-budget-btn" variant="outline" onClick={exportBudget} className="rounded-sm gap-1.5 text-xs">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />Budget CSV
          </Button>
        </div>
      </div>

      {/* Bank Balance + Net Position (live from TrueLayer) */}
      <BankBalanceCard api={api} totalDebt={dashboard.total_debt} />

      {/* Promo Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-2" data-testid="promo-warnings">
          {warnings.map((w, i) => (
            <div key={i} className={`flex items-start gap-3 p-3 rounded-sm border ${w.status === "expired" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}>
              {w.status === "expired" ? <ShieldAlert className="w-4 h-4 text-red-600 mt-0.5 shrink-0" strokeWidth={1.5} /> : <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.5} />}
              <div>
                <p className={`text-sm font-medium ${w.status === "expired" ? "text-red-800" : "text-amber-800"}`}>
                  {w.name}: 0% promo {w.status === "expired" ? "has expired" : `expires ${w.promo_until}`}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  APR will be {w.apr_after}.
                  {w.is_deferred && <span className="font-semibold text-red-700 ml-1">DEFERRED INTEREST — pay in full before expiry.</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hero Cards - 5 cards with Progress Ring */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
        {/* Progress Ring — Hero Card */}
        <div data-testid="dashboard-progress-ring" className="bg-white border border-green-200 rounded-sm p-4 flex flex-col items-center justify-center relative">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 self-start">Debt Progress</span>
          <div className="relative">
            <ProgressRing pct={paidPct} size={96} strokeWidth={8} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono-data text-xl font-bold" style={{ color: "var(--chart-positive)" }}>{paidPct}%</span>
              <span className="text-[10px] text-gray-400 uppercase">Cleared</span>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-1 font-mono-data text-center">{fmt(paidOff)} of {fmt(originalTotal)}</p>
        </div>

        <StatCard title="Total Debt" value={fmt(dashboard.total_debt)} sub={`${dashboard.debts_tracked} debts tracked`} icon={PoundSterling} variant="danger" testId="dashboard-total-debt" />
        <StatCard title="Est. Next Min" value={fmt(estNextMin)} sub="Dynamic (% of balance)" icon={TrendingDown} variant="warning" testId="dashboard-monthly-min" />
        <StatCard title="Avg Interest Rate" value={`${dashboard.weighted_avg_rate || 0}%`} sub="Weighted across all debts" icon={Percent} variant="info" testId="dashboard-wair" />
        <StatCard title="Debt-Free Date" value={dashboard.debt_free_date || "No plan"} sub={dashboard.plan_end_date !== "No plan" ? `Plan ends: ${dashboard.plan_end_date}` : undefined} icon={CalendarClock} variant="success" testId="dashboard-debt-free" />
      </div>

      {/* Income/Expense/Debt Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-white border border-green-200 rounded-sm p-4">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Total Income</span>
          <p data-testid="dashboard-total-income" className="font-mono-data text-xl font-semibold text-green-700 mt-1">{fmt(dashboard.total_income)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Bills & Expenses</span>
          <p data-testid="dashboard-total-expenses" className="font-mono-data text-xl font-semibold text-red-700 mt-1">{fmt(dashboard.total_expenses)}</p>
        </div>
        <div className="bg-white border border-blue-200 rounded-sm p-4">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Debt Payments</span>
          <p data-testid="dashboard-total-debt-payments" className="font-mono-data text-xl font-semibold text-blue-700 mt-1">{fmt(dashboard.total_debt_payments || 0)}</p>
        </div>
        <div className={`bg-white border rounded-sm p-4 ${dashboard.net_balance >= 0 ? "border-green-200" : "border-red-200"} relative`}>
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Net Balance</span>
          <div className="flex items-center gap-2 mt-1">
            <p data-testid="dashboard-net-balance" className={`font-mono-data text-xl font-semibold ${dashboard.net_balance >= 0 ? "text-green-700" : "text-red-700"}`}>{fmt(dashboard.net_balance)}</p>
            {dashboard.net_balance < 0 && (
              <button data-testid="leak-warning-btn" onClick={() => setLeakOpen(!leakOpen)}
                className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider transition-colors cursor-pointer ${dashboard.net_balance < -100 ? "border-red-400 bg-red-100 text-red-700 animate-pulse" : "border-red-300 bg-red-50 text-red-600"}`}>
                Leak
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Leak Report */}
      {leakOpen && dashboard.net_balance < 0 && (
        <div data-testid="leak-report" className="border border-red-200 rounded-sm bg-red-50/30 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-red-700">Financial Leak Report</h3>
            <button onClick={() => setLeakOpen(false)} className="p-1 hover:bg-red-100 rounded-sm"><X className="w-4 h-4 text-red-400" /></button>
          </div>
          <p className="text-xs text-gray-600">Your outgoings exceed income by <span className="font-mono-data font-semibold text-red-700">{fmt(Math.abs(dashboard.net_balance))}/mo</span>. Here are potential cuts, ranked by impact vs your debt APR:</p>
          <div className="space-y-2">
            {(() => {
              // Get highest debt APR
              const maxApr = debts.length > 0 ? Math.max(...debts.map(d => parseFloat(d.apr_after?.replace("% APR","").replace("%","") || "0"))) : 0;
              // Get non-essential expenses (not income, not debt payments)
              const expenses = budget.filter(b => b.category === "expense").sort((a, b) => b.actual - a.actual);
              return expenses.slice(0, 6).map((exp, i) => {
                const monthlyInterestCost = (exp.actual * maxApr / 100 / 12);
                return (
                  <div key={i} className="flex items-center justify-between p-2 rounded-sm border border-red-100 bg-white">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800 truncate block">{exp.description}</span>
                      <span className="text-xs text-gray-500">{fmt(exp.actual)}/mo</span>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <span className="text-xs text-red-600 font-mono-data font-semibold block">Cost of keeping: {fmt(monthlyInterestCost)}/mo</span>
                      <span className="text-[10px] text-gray-400">vs redirecting to {maxApr}% APR debt</span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          <p className="text-[10px] text-gray-400 pt-2 border-t border-red-100">
            "Cost of keeping" = what this amount would save if redirected to your highest-APR debt ({debts.length > 0 ? debts.reduce((h, d) => parseFloat(d.apr_after?.replace("% APR","").replace("%","") || "0") > parseFloat(h.apr_after?.replace("% APR","").replace("%","") || "0") ? d : h).name : "none"}).
          </p>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <CollapsibleSection id="dash-cashflow-chart" title="Monthly Cashflow Breakdown">
          <div className="p-4 flex flex-col sm:flex-row items-center gap-4" style={{ minHeight: 240 }}>
            <div className="relative shrink-0" style={{ width: 200, height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={88} innerRadius={52} dataKey="value" paddingAngle={3} stroke={theme === "light" ? "#fff" : "#000"} strokeWidth={3} label={false} labelLine={false}>
                    {pieData.map((_, idx) => <Cell key={idx} fill={pieColors[idx % pieColors.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Net</span>
                <span className={`font-mono-data text-lg font-bold ${dashboard.net_balance >= 0 ? "text-green-700" : "text-red-700"}`}>
                  {fmt(dashboard.net_balance)}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 pointer-events-none select-none">
              {pieData.map((d, idx) => (
                <div key={idx} className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: pieColors[idx % pieColors.length] }} />
                  <div>
                    <span className="text-sm text-gray-700 font-medium">{d.name}</span>
                    <span className="font-mono-data text-sm text-gray-900 font-semibold ml-2">{fmt(d.value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection id="dash-debt-chart" title="Debt Payoff Progress">
          <div className="p-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={debtBarData} layout="vertical" margin={{ left: 5, right: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cc.grid} horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: cc.text }} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 10, fill: cc.text }} />
                <Tooltip formatter={(v, name) => [fmt(v), name === "remaining" ? "Remaining" : "Paid Off"]} />
                <Legend wrapperStyle={{ fontSize: 10, pointerEvents: "none", userSelect: "none" }} iconType="circle" />
                <Bar dataKey="paid" name="Paid Off" stackId="debt" fill={cc.barPaid} radius={[0, 0, 0, 0]} />
                <Bar dataKey="remaining" name="Remaining" stackId="debt" fill={cc.barPrimary} radius={[0, 2, 2, 0]}
                  label={{ position: "right", fontSize: 9, fill: cc.text, formatter: (v) => {
                    const item = debtBarData.find(d => d.remaining === v);
                    return item ? item.pctLabel : "";
                  }}}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CollapsibleSection>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <CollapsibleSection id="dash-top-debts" title="Top Debts by Balance">
          <div className="overflow-x-auto">
            <table className="spreadsheet-table w-full">
              <thead><tr><th className="text-left">Debt</th><th className="text-right">Balance</th><th className="text-center">0% Until</th><th className="text-right">APR After</th><th className="text-right">Min</th></tr></thead>
              <tbody>
                {topDebts.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div>
                        <span className="font-medium text-gray-900">{d.name}</span>
                        {d.min_payment_type === "percentage" && (
                          <span className="ml-1.5 text-[9px] px-1 py-0 rounded-sm border border-gray-300 bg-gray-100 text-gray-500 font-mono-data">{d.min_payment_pct}% Min</span>
                        )}
                        {d.is_deferred_interest && <Badge className="ml-1.5 border border-amber-400 text-amber-700 bg-amber-50 text-[10px] px-1.5 py-0 rounded-sm" variant="outline">DEFERRED</Badge>}
                      </div>
                      <PromoProgressBar promo_until={d.promo_until} promo_status={d.promo_status} />
                    </td>
                    <td className="numeric">{fmt(d.total_balance)}</td>
                    <td className="text-center"><span className={`font-mono-data text-xs ${d.promo_status === "expired" ? "text-red-600" : d.promo_status === "expiring_soon" ? "text-amber-600" : "text-gray-600"}`}>{d.promo_until || "-"}</span></td>
                    <td className="numeric text-xs">{d.apr_after}</td>
                    <td className="numeric">{fmt(d.statement_min)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>

        <CollapsibleSection id="dash-recent-budget" title="Recent Budget Entries">
          <div className="overflow-x-auto">
            <table className="spreadsheet-table w-full">
              <thead><tr><th className="text-left">Date</th><th className="text-left">Description</th><th className="text-center">Type</th><th className="text-right">Actual</th></tr></thead>
              <tbody>
                {recentBudget.map((b) => (
                  <tr key={b.id}>
                    <td className="font-mono-data text-xs text-gray-600">{fmtDate(b.date)}</td>
                    <td className="text-gray-900">{b.description}</td>
                    <td className="text-center"><Badge className={`text-[10px] px-1.5 py-0 rounded-sm font-mono-data border ${b.category === "income" ? "border-green-300 text-green-700 bg-green-50" : b.category === "debt_payment" ? "border-blue-300 text-blue-700 bg-blue-50" : "border-gray-300 text-gray-600 bg-gray-50"}`} variant="outline">{b.category === "income" ? "Income" : b.category === "debt_payment" ? "Debt" : "Bill"}</Badge></td>
                    <td className={`numeric ${b.category === "income" ? "text-green-700" : b.category === "debt_payment" ? "text-blue-700" : "text-red-700"}`}>{b.actual > 0 ? fmt(b.actual) : "-"}</td>
                  </tr>
                ))}
                {recentBudget.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-gray-400 py-8">No budget entries yet. Add your first entry in Monthly Budget.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
